import { useCallback, useEffect, useMemo, useReducer } from "react";
import { handleValidations, detachValidate, isValidMapErrors } from "./handleValidations";

function useValidations() {
	// Validations controllers
	const [errors, handleValidation] = useReducer(handleValidations, new Map());

	// Valid specific errors
	const hasErrors = useCallback((key) => Boolean(errors?.get(key)) && !errors.get(key)?.isValid, [errors]);

	// Valid all errors map
	const isValid = useMemo(() => isValidMapErrors(errors), [errors]);

	// Get specific section errors messages
	const getSectionsErrorMessages = useCallback(
		(keys = []) => {
			const messages = [];
			if (keys.length && errors.size) {
				for (const key of keys) {
					const error = key ? errors.get(key) : null;
					if (!error?.isValid) {
						const message = errors.get(key)?.definition?.message;
						if (message) {
							messages.push(message);
						}
					}
				}
			}
			return [...new Set(messages)];
		},
		[errors]
	);

	// Get all errors
	const getErrors = useCallback(() => {
		const result = new Map();
		for (const [key, value] of errors) {
			result.set(key, value?.errors || []);
		}
		return result;
	}, [errors]);

	// Get all concatenated errors
	const getConcatenatedErrors = useCallback(() => {
		if (errors.size) {
			const result = [];
			for (const [key, value] of errors) {
				if (key && value?.errors?.length) {
					for (let i = 0; i < value.errors.length; i++) {
						result[i] = { ...(result?.[i] || null), ...(value?.errors?.[i] || null) };
					}
				}
			}
			return result;
		}
		return [];
	}, [errors]);

	// Get all messages
	const getErrorsMessages = useCallback(() => {
		const messages = [];
		for (const [key, error] of errors) {
			if (key && !error?.isValid) {
				messages.push(error?.definition.message || "");
			}
		}
		return [...new Set(messages)];
	}, [errors]);

	// Validate
	const validate = useCallback((validations) => {
		if (validations.length) {
			handleValidation({ type: "VALIDATE", validations });
		}
	}, []);

	// Clear (private -> don't use outside)
	const __clearErrors = useCallback(() => {
		handleValidation({ type: "CLEAR" });
	}, []);

	useEffect(() => {
		return () => {
			__clearErrors();
		};
	}, [__clearErrors]);

	return {
		errors: getErrors(),
		concatenatedErrors: getConcatenatedErrors(),
		isValid,
		getErrorsMessages,
		getSectionsErrorMessages,
		hasErrors,
		validate,
	};
}

export { useValidations, detachValidate, isValidMapErrors };
